/**
 * A date field with the application's own calendar.
 *
 * `<input type="date">` was the right first answer - it is the platform's
 * control, it validates, and it costs nothing. What it cannot do is look like
 * this application: the popup is drawn by the browser in its own layer, so no
 * stylesheet reaches it. On Chrome for Windows in Japanese that means a grey
 * grid with a 令和 era heading and blue system highlights, opening out of a
 * pink card. The same trade the Select made, for the same reason.
 *
 * What replaces it has to earn the swap, so it keeps what the native control
 * gave:
 *
 *   - the text stays typeable. The field is an <input>, not a button: a date
 *     typed as 2026-11-07 or 2026/11/07 is accepted and normalised on blur,
 *     which is faster than clicking through a grid for anyone who knows the
 *     date they want.
 *   - the grid is a real grid. `role="grid"`, one tab stop, arrows to move by a
 *     day, PageUp/PageDown by a month, Home/End to the ends of the week, Enter
 *     to choose, Escape to leave - the keyboard contract a date picker is
 *     expected to honour.
 *   - the value is ISO. `YYYY-MM-DD` in and out, the same string the server
 *     takes, so nothing in a caller has to parse a locale.
 *
 * Dates are handled as local Y/M/D numbers throughout, never as UTC. A
 * calendar cell is a day in the reader's timezone; going through `Date.UTC` or
 * `toISOString()` shifts it by nine hours in Japan and shows the wrong month
 * for the first and last day of every one.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { popupHost, popupOrigin } from '../popup-host.ts';
import {
  WEEKDAYS,
  addDays,
  addMonths,
  iso,
  monthGrid,
  parseIso,
  parseTyped,
  sameDay,
} from '../dates.ts';

const monthLabel = (date: Date): string =>
  `${date.getFullYear()}年 ${date.getMonth() + 1}月`;

const fullLabel = (date: Date): string =>
  `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${WEEKDAYS[date.getDay()]}）`;

interface Position {
  left: number;
  top: number;
  above: boolean;
}

export function DateInput({
  id,
  value,
  onChange,
  /** ISO date; days before it cannot be chosen. */
  min,
  /** ISO date; days after it cannot be chosen. */
  max,
  disabled,
  placeholder = 'YYYY-MM-DD',
  ariaLabel,
  /** Shown under the grid as a one-press shortcut. Omit to hide it. */
  todayLabel = '今日',
  /** Shown beside it, clearing the value. Omit when the field is required. */
  clearLabel = '未設定にする',
}: {
  id?: string;
  /** ISO `YYYY-MM-DD`, or '' for unset. */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  todayLabel?: string | null;
  clearLabel?: string | null;
}) {
  const gridId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  /** What is in the box while it is being typed in. */
  const [text, setText] = useState(value);

  const today = new Date();
  const selected = parseIso(value);
  const minDate = min ? parseIso(min) : null;
  const maxDate = max ? parseIso(max) : null;

  /** The day the keyboard is on. Never the same thing as the chosen day. */
  const [cursor, setCursor] = useState<Date>(selected ?? today);
  const [month, setMonth] = useState<Date>(
    new Date((selected ?? today).getFullYear(), (selected ?? today).getMonth(), 1),
  );

  // The field follows the value when the form is reseeded from the server.
  useEffect(() => setText(value), [value]);

  const disabledDay = useCallback(
    (day: Date): boolean =>
      (minDate !== null && day < minDate) || (maxDate !== null && day > maxDate),
    [minDate, maxDate],
  );

  const measure = useCallback(() => {
    const anchor = wrapRef.current;
    if (!anchor) return;
    const box = anchor.getBoundingClientRect();
    // Zero unless the host establishes a containing block; see popupOrigin.
    const origin = popupOrigin(popupHost(anchor));
    // 330px is the popup's height; flipping avoids a calendar off the bottom.
    const room = window.innerHeight - box.bottom;
    const above = room < 330 && box.top > room;
    setPosition({
      left: box.left - origin.left,
      top: (above ? box.top : box.bottom + 4) - origin.top,
      above,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  /*
   * Focus the grid once it exists - the same two-render problem the Select has:
   * the popup is not in the DOM until a position has been measured.
   */
  const focused = useRef(false);
  useEffect(() => {
    if (!open) {
      focused.current = false;
      return;
    }
    if (position && !focused.current) {
      popupRef.current?.querySelector<HTMLElement>('.datepicker__grid')?.focus();
      focused.current = true;
    }
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const openAt = () => {
    const start = parseIso(text) ?? selected ?? today;
    setCursor(start);
    setMonth(new Date(start.getFullYear(), start.getMonth(), 1));
    setOpen(true);
  };

  const choose = (day: Date) => {
    if (disabledDay(day)) return;
    onChange(iso(day));
    setText(iso(day));
    setOpen(false);
    inputRef.current?.focus();
  };

  const moveCursor = (next: Date) => {
    setCursor(next);
    setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  /*
   * Typed text becomes the value on blur, not on every keystroke: committing
   * mid-type would fight the person writing 2026 by reformatting after 2, 20
   * and 202.
   */
  const commitText = () => {
    if (text.trim() === '') {
      onChange('');
      return;
    }
    const parsed = parseTyped(text, today);
    if (parsed && !disabledDay(parsed)) {
      onChange(iso(parsed));
      setText(iso(parsed));
    } else {
      // Unreadable, or out of range: put back the value that is actually held
      // rather than leaving a string the form does not have.
      setText(value);
    }
  };

  function onGridKeyDown(event: React.KeyboardEvent) {
    const jump = (next: Date) => {
      event.preventDefault();
      moveCursor(next);
    };
    switch (event.key) {
      case 'ArrowLeft':
        return jump(addDays(cursor, -1));
      case 'ArrowRight':
        return jump(addDays(cursor, 1));
      case 'ArrowUp':
        return jump(addDays(cursor, -7));
      case 'ArrowDown':
        return jump(addDays(cursor, 7));
      case 'Home':
        return jump(addDays(cursor, -cursor.getDay()));
      case 'End':
        return jump(addDays(cursor, 6 - cursor.getDay()));
      case 'PageUp':
        return jump(addMonths(cursor, event.shiftKey ? -12 : -1));
      case 'PageDown':
        return jump(addMonths(cursor, event.shiftKey ? 12 : 1));
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(cursor);
        return;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        inputRef.current?.focus();
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
        return;
    }
  }

  const days = monthGrid(month);

  return (
    <>
      <div className="datefield" ref={wrapRef}>
        <input
          ref={inputRef}
          id={id}
          className="datefield__input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          disabled={disabled}
          value={text}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          onChange={(event) => setText(event.target.value)}
          onBlur={commitText}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitText();
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              openAt();
            }
          }}
        />
        <button
          type="button"
          className="datefield__button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={open ? 'カレンダーを閉じる' : 'カレンダーを開く'}
          onClick={() => (open ? setOpen(false) : openAt())}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" aria-hidden>
            <rect x="3" y="5" width="18" height="16" rx="3" />
            <path d="M3 10h18M8 3v4M16 3v4" />
          </svg>
        </button>
      </div>

      {open &&
        position &&
        createPortal(
          <div
            ref={popupRef}
            className="datepicker"
            role="dialog"
            aria-label="日付を選ぶ"
            style={{
              left: Math.min(position.left, window.innerWidth - 300),
              ...(position.above
                ? { bottom: window.innerHeight - position.top + 4 }
                : { top: position.top }),
            }}
          >
            <div className="datepicker__head">
              <button
                type="button"
                className="datepicker__nav"
                aria-label="前の月"
                onClick={() => setMonth(addMonths(month, -1))}
              >
                ‹
              </button>
              <div className="datepicker__month" aria-live="polite">
                {monthLabel(month)}
              </div>
              <button
                type="button"
                className="datepicker__nav"
                aria-label="次の月"
                onClick={() => setMonth(addMonths(month, 1))}
              >
                ›
              </button>
            </div>

            <div className="datepicker__weekdays" aria-hidden>
              {WEEKDAYS.map((day) => (
                <span key={day} className="datepicker__weekday">
                  {day}
                </span>
              ))}
            </div>

            {/*
              One tab stop for the whole grid, with the active day carried by
              aria-activedescendant. Forty-two focusable buttons would mean
              forty-two presses of Tab to leave a calendar.
            */}
            <div
              className="datepicker__grid"
              role="grid"
              tabIndex={0}
              aria-activedescendant={`${gridId}-${iso(cursor)}`}
              onKeyDown={onGridKeyDown}
            >
              {days.map((day) => {
                const outside = day.getMonth() !== month.getMonth();
                const off = disabledDay(day);
                return (
                  <div
                    key={iso(day)}
                    id={`${gridId}-${iso(day)}`}
                    role="gridcell"
                    aria-selected={selected ? sameDay(day, selected) : false}
                    aria-disabled={off}
                    aria-label={fullLabel(day)}
                    className={[
                      'datepicker__day',
                      outside ? 'datepicker__day--outside' : '',
                      off ? 'datepicker__day--off' : '',
                      sameDay(day, today) ? 'datepicker__day--today' : '',
                      selected && sameDay(day, selected) ? 'datepicker__day--selected' : '',
                      sameDay(day, cursor) ? 'datepicker__day--cursor' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseMove={() => setCursor(day)}
                    onClick={() => choose(day)}
                  >
                    {day.getDate()}
                  </div>
                );
              })}
            </div>

            {(todayLabel || clearLabel) && (
              <div className="datepicker__foot">
                {todayLabel && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={disabledDay(today)}
                    onClick={() => choose(today)}
                  >
                    {todayLabel}
                  </button>
                )}
                {clearLabel && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      onChange('');
                      setText('');
                      setOpen(false);
                      inputRef.current?.focus();
                    }}
                  >
                    {clearLabel}
                  </button>
                )}
              </div>
            )}
          </div>,
          popupHost(wrapRef.current),
        )}
    </>
  );
}
